import { Suspense, lazy, memo } from 'react';
import { Box, Spinner, Center } from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import { ModelCategoryV1 } from 'pages/ModelOverviewPage/blockv1/types';
import { Flags } from 'models/Global.interface';
import BlockList from 'pages/ModelOverviewPage/block';
import { isDataRevampEnabled } from 'utils/dataRevampFlag';
import { isDimensionsRevampEnabled } from 'utils/dimensionsRevampFlag';
import { isDashboardRevampEnabled } from 'utils/dashboardRevampFlag';

import { TabType } from '../types';
import { HEADER_CONFIG } from '../constants';

import { BlocksTabModule } from './blocksTabModule';

// Lazy load heavy components
const Dashboard = lazy(() => import('pages/ModelOverviewPage/Dashboard-v2'));
// This branch's real Dashboard redesign — Dashboard-v2 above stays the
// untouched original fallback.
const DashboardRevamp = lazy(() => import('pages/ModelOverviewPage/DashboardRevamp'));
const Dimensions = lazy(() => import('pages/ModelOverviewPage/dimensions'));
// DimensionsRevampV2's content now lives at this same path — the folder was
// swapped in place rather than renamed at every import site, so the V1
// header's own TabContent (which already pointed at ./DimensionsRevamp)
// picked up the new UI without any change there.
const DimensionsRevamp = lazy(() => import('pages/ModelOverviewPage/DimensionsRevamp'));
const DataImport = lazy(() => import('pages/ModelOverviewPage/Import'));
const DataRevamp = lazy(() => import('pages/ModelOverviewPage/data-revamp-new'));

interface TabContentProperties {
  activeTab: TabType;
  blocks?: any[];
  categories?: ModelCategoryV1[];
  flags?: Flags;
  isLoadingBlocks?: boolean;
  isBlocksError?: boolean;
}

const LoadingFallback = () => (
  <Center h='200px'>
    <Spinner size='xl' color='#745BE3' thickness='4px' />
  </Center>
);

const TabContent = memo(
  ({
    activeTab,
    blocks,
    categories,
    flags,
    isLoadingBlocks = false,
    isBlocksError = false,
  }: TabContentProperties) => {
    // const isTablet = Boolean((useMediaQuery('(max-width: 768px)') as any)[0]);

    const blocksManagementOn = Boolean(flags?.blocksManagement);
    const isDashboardRevamp = isDashboardRevampEnabled(flags);
    const isDimensionsRevamp = isDimensionsRevampEnabled(flags);
    const isDataRevamp = isDataRevampEnabled(flags);
    // One source of truth for "this tab renders the revamped surface". The
    // same three-way condition used to be spelled out separately for bg, px
    // and pt, so the Data tab was added to none of them and kept the old
    // purple body gradient under an otherwise fully revamped page.
    const isRevampedSurface =
      (blocksManagementOn && activeTab === 'blocks') ||
      (activeTab === 'dimensions' && isDimensionsRevamp) ||
      (activeTab === 'dashboard' && isDashboardRevamp) ||
      (activeTab === 'import' && isDataRevamp);

    return (
      <Box
        // Derived from the header's own constant rather than a copy of its
        // number. The two had already drifted — the header declared 53px but,
        // inside its `zoom: 0.9` box, painted 47.7px, so this subtracted ~5.3px
        // too much from every page in the app.
        height={`calc(100vh - ${HEADER_CONFIG.HEIGHT})`}
        overflow='auto'
        bg={
          isRevampedSurface
            ? 'linear-gradient(180deg, #FCFAF8 14.67%, #F5F2FF 48.8%, #FFF0FC 100%)'
            : 'gradient.body'
        }
        px={isRevampedSurface ? 0 : 4}
        pt={isRevampedSurface ? 0 : 4}
      >
        {/* <Box px={isTablet ? 4 : 7} pt={3}>
        <BreadCrumbComponent hasBlock={false} />
      </Box> */}
        {/* The revamped header adds a 10px lead-in above this row; the legacy
          one doesn't. Both offsets now hang off HEADER_CONFIG.HEIGHT so they
          can't drift from the real header height again. */}
        <Box
          height={`calc(100vh - ${
            flags?.uiRevamp ? `(${HEADER_CONFIG.HEIGHT} + 10px)` : HEADER_CONFIG.HEIGHT
          })`}
        >
          <Suspense fallback={<LoadingFallback />}>
            {activeTab === 'dashboard' &&
              (isDashboardRevampEnabled(flags) ? <DashboardRevamp /> : <Dashboard />)}
            {activeTab === 'blocks' &&
              (blocksManagementOn ? (
                <BlocksTabModule
                  blocks={blocks || []}
                  categories={categories || []}
                  isLoading={isLoadingBlocks}
                  isError={isBlocksError}
                />
              ) : (
                <BlockList blocks={blocks || []} />
              ))}
            {activeTab === 'dimensions' &&
              (isDimensionsRevampEnabled(flags) ? <DimensionsRevamp /> : <Dimensions />)}
            {activeTab === 'import' && (isDataRevamp ? <DataRevamp /> : <DataImport />)}
          </Suspense>
        </Box>
      </Box>
    );
  },
);

TabContent.displayName = 'TabContent';

export default withLDConsumer()(TabContent);
